import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { http, money, formatApiError, laundryLabels, laundryKeys } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2, Edit3, Home } from "lucide-react";
import { toast } from "sonner";

const emptyProp = () => ({ name: "", city: "", address: "", owner_id: "", company: "", notes: "" });
const DEFAULT_LC = {
  double_sheets_per_double_bed: 2, pillowcases_per_double_bed: 2,
  single_sheets_per_single_bed: 2, pillowcases_per_single_bed: 1,
  large_towels_per_person: 1, medium_towels_per_person: 1,
  bath_mats_per_bathroom: 1, standard_persons: null,
};
const emptyApt = () => ({
  name: "", property_id: "", owner_id: "", address: "", city: "",
  capacity: 2, bathrooms: 1, double_beds: 1, single_beds: 0,
  cleaning_cost: 0, management_percent: 15, management_base: "netto_ota",
  active: true, notes: "", laundry_config: { ...DEFAULT_LC },
});

const LC_LABELS = {
  double_sheets_per_double_bed: "Lenzuola matrimoniali / letto matr.",
  pillowcases_per_double_bed: "Federe / letto matrimoniale",
  single_sheets_per_single_bed: "Lenzuola singole / letto singolo",
  pillowcases_per_single_bed: "Federe / letto singolo",
  large_towels_per_person: "Asciugamani grandi / persona",
  medium_towels_per_person: "Asciugamani medi / persona",
  bath_mats_per_bathroom: "Tappeti bagno / bagno",
  standard_persons: "Persone standard (vuoto = capienza)",
};

export default function Appartamenti() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("apts");
  const [apts, setApts] = useState([]);
  const [props, setProps] = useState([]);
  const [owners, setOwners] = useState([]);
  const [openA, setOpenA] = useState(false);
  const [openP, setOpenP] = useState(false);
  const [formA, setFormA] = useState(emptyApt());
  const [formP, setFormP] = useState(emptyProp());
  const [editA, setEditA] = useState(null);
  const [editP, setEditP] = useState(null);

  const load = async () => {
    const [a, p, o] = await Promise.all([http.get("/apartments"), http.get("/properties"), http.get("/owners")]);
    setApts(a.data); setProps(p.data); setOwners(o.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (params.get("new") === "1") { setFormA(emptyApt()); setEditA(null); setOpenA(true); params.delete("new"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const saveA = async (e) => {
    e.preventDefault();
    const p = { ...formA };
    ["capacity", "bathrooms", "double_beds", "single_beds"].forEach((k) => { p[k] = Number(p[k]) || 0; });
    p.cleaning_cost = Number(p.cleaning_cost) || 0;
    p.management_percent = Number(p.management_percent) || 0;
    p.laundry_config = { ...p.laundry_config };
    Object.keys(p.laundry_config).forEach((k) => {
      if (k === "standard_persons") p.laundry_config[k] = p.laundry_config[k] ? Number(p.laundry_config[k]) : null;
      else p.laundry_config[k] = Number(p.laundry_config[k]) || 0;
    });
    ["property_id", "owner_id"].forEach((k) => { if (!p[k]) p[k] = null; });
    try {
      if (editA) await http.put(`/apartments/${editA}`, p); else await http.post("/apartments", p);
      toast.success("Salvato"); setOpenA(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveP = async (e) => {
    e.preventDefault();
    const p = { ...formP }; if (!p.owner_id) p.owner_id = null;
    try {
      if (editP) await http.put(`/properties/${editP}`, p); else await http.post("/properties", p);
      toast.success("Salvato"); setOpenP(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delA = async (id) => { if (!window.confirm("Eliminare l'appartamento?")) return; await http.delete(`/apartments/${id}`); load(); };
  const delP = async (id) => { if (!window.confirm("Eliminare la struttura?")) return; await http.delete(`/properties/${id}`); load(); };
  const propName = (id) => props.find((p) => p.id === id)?.name || "-";
  const ownerName = (id) => owners.find((o) => o.id === id)?.name || "-";

  const updLC = (k, v) => setFormA((f) => ({ ...f, laundry_config: { ...f.laundry_config, [k]: v } }));

  return (
    <div className="space-y-6" data-testid="appartamenti-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--terracotta)] font-semibold">Parco</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--teal-deep)]">Strutture & Appartamenti</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setFormP(emptyProp()); setEditP(null); setOpenP(true); }} className="rounded-xl min-h-[44px]"><Plus className="h-4 w-4 mr-1" /> Struttura</Button>
          <Button onClick={() => { setFormA(emptyApt()); setEditA(null); setOpenA(true); }} className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)] rounded-xl min-h-[44px]" data-testid="new-apt-btn"><Plus className="h-4 w-4 mr-1" /> Appartamento</Button>
        </div>
      </div>

      <div className="flex gap-2">
        <button onClick={() => setTab("apts")} data-testid="tab-apts" className={`px-4 py-2 rounded-full text-sm font-semibold border min-h-[44px] ${tab==="apts"?"bg-[var(--teal-deep)] text-white border-[var(--teal-deep)]":"bg-white text-slate-600 border-slate-200"}`}>Appartamenti ({apts.length})</button>
        <button onClick={() => setTab("props")} data-testid="tab-props" className={`px-4 py-2 rounded-full text-sm font-semibold border min-h-[44px] ${tab==="props"?"bg-[var(--teal-deep)] text-white border-[var(--teal-deep)]":"bg-white text-slate-600 border-slate-200"}`}>Strutture ({props.length})</button>
      </div>

      {tab === "apts" && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {apts.length === 0 && <div className="col-span-full text-center text-slate-400 py-10">Nessun appartamento. Crea il primo!</div>}
          {apts.map((a) => (
            <Card key={a.id} className="rounded-2xl p-5 hover:shadow-lg transition" data-testid={`apt-${a.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-xl bg-teal-100 text-teal-800 flex items-center justify-center shrink-0"><Home className="h-5 w-5" /></div>
                  <div className="min-w-0">
                    <Link to={`/appartamenti/${a.id}`} className="font-display text-lg font-bold text-slate-900 hover:underline block truncate">{a.name}</Link>
                    <div className="text-xs text-slate-500 truncate">{propName(a.property_id)}{a.city ? ` · ${a.city}` : ""}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setFormA({ ...emptyApt(), ...a, laundry_config: { ...DEFAULT_LC, ...(a.laundry_config || {}) } }); setEditA(a.id); setOpenA(true); }} data-testid={`edit-apt-${a.id}`} className="p-1.5 text-slate-500 hover:text-teal-700"><Edit3 className="h-4 w-4" /></button>
                  <button onClick={() => delA(a.id)} className="p-1.5 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {!a.active && <div className="mt-2 text-xs font-semibold text-rose-600">NON ATTIVO</div>}
              <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                <div><div className="text-slate-500">Proprietario</div><div className="font-medium truncate">{ownerName(a.owner_id)}</div></div>
                <div><div className="text-slate-500">Gestione</div><div className="font-medium">{a.management_percent}%</div></div>
                <div><div className="text-slate-500">Tariffa pulizia</div><div className="font-medium">{money(a.cleaning_cost)}</div></div>
                <div><div className="text-slate-500">Ospiti / Letti</div><div className="font-medium">{a.capacity} · {a.double_beds}M+{a.single_beds}S</div></div>
              </div>
              <Link to={`/appartamenti/${a.id}`} data-testid={`open-apt-${a.id}`} className="mt-4 block text-center text-sm font-semibold text-[var(--teal-deep)] hover:underline">Scheda completa →</Link>
            </Card>
          ))}
        </div>
      )}

      {tab === "props" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {props.length === 0 && <div className="col-span-full text-center text-slate-400 py-10">Nessuna struttura.</div>}
          {props.map((p) => (
            <Card key={p.id} className="rounded-2xl p-5" data-testid={`prop-${p.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-display text-lg font-bold text-slate-900">{p.name}</div>
                  <div className="text-sm text-slate-500">{p.city} · {p.address}</div>
                  <div className="text-xs text-slate-400 mt-1">Proprietario: {ownerName(p.owner_id)}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => { setFormP({ ...emptyProp(), ...p }); setEditP(p.id); setOpenP(true); }} className="p-1.5 text-slate-500 hover:text-teal-700"><Edit3 className="h-4 w-4" /></button>
                  <button onClick={() => delP(p.id)} className="p-1.5 text-slate-500 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="mt-3 text-xs text-slate-500">Appartamenti collegati: {apts.filter((a) => a.property_id === p.id).length}</div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={openA} onOpenChange={setOpenA}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editA ? "Modifica" : "Nuovo"} appartamento</DialogTitle></DialogHeader>
          <form onSubmit={saveA} className="space-y-5" data-testid="apt-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Nome appartamento</Label><Input value={formA.name} onChange={(e) => setFormA({ ...formA, name: e.target.value })} required data-testid="apt-name" /></div>
              <div><Label>Struttura</Label>
                <Select value={formA.property_id || ""} onValueChange={(v) => setFormA({ ...formA, property_id: v })}><SelectTrigger data-testid="apt-property"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Proprietario</Label>
                <Select value={formA.owner_id || ""} onValueChange={(v) => setFormA({ ...formA, owner_id: v })}><SelectTrigger data-testid="apt-owner"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="flex items-center gap-3 pt-6"><Switch checked={formA.active} onCheckedChange={(v) => setFormA({ ...formA, active: v })} data-testid="apt-active" /><Label>Attivo</Label></div>
              <div><Label>Indirizzo</Label><Input value={formA.address || ""} onChange={(e) => setFormA({ ...formA, address: e.target.value })} /></div>
              <div><Label>Città</Label><Input value={formA.city || ""} onChange={(e) => setFormA({ ...formA, city: e.target.value })} /></div>
              <div><Label>Capienza massima</Label><Input type="number" min="1" value={formA.capacity} onChange={(e) => setFormA({ ...formA, capacity: e.target.value })} data-testid="apt-capacity" /></div>
              <div><Label>Numero bagni</Label><Input type="number" min="0" value={formA.bathrooms} onChange={(e) => setFormA({ ...formA, bathrooms: e.target.value })} data-testid="apt-bathrooms" /></div>
              <div><Label>Letti matrimoniali</Label><Input type="number" min="0" value={formA.double_beds} onChange={(e) => setFormA({ ...formA, double_beds: e.target.value })} data-testid="apt-double-beds" /></div>
              <div><Label>Letti singoli</Label><Input type="number" min="0" value={formA.single_beds} onChange={(e) => setFormA({ ...formA, single_beds: e.target.value })} data-testid="apt-single-beds" /></div>
              <div><Label>Tariffa pulizia standard (€)</Label><Input type="number" step="0.01" value={formA.cleaning_cost} onChange={(e) => setFormA({ ...formA, cleaning_cost: e.target.value })} data-testid="apt-cleaning-cost" /></div>
              <div><Label>% gestione PuraVida Home</Label><Input type="number" step="0.01" value={formA.management_percent} onChange={(e) => setFormA({ ...formA, management_percent: e.target.value })} data-testid="apt-mgmt-pct" /></div>
              <div><Label>Base di calcolo gestione predefinita</Label>
                <Select value={formA.management_base} onValueChange={(v) => setFormA({ ...formA, management_base: v })}>
                  <SelectTrigger data-testid="apt-mgmt-base"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="netto_ota">Netto OTA</SelectItem>
                    <SelectItem value="lordo_ota">Lordo OTA</SelectItem>
                    <SelectItem value="netto_totale">Netto totale inclusi diretti</SelectItem>
                    <SelectItem value="custom">Importo personalizzato</SelectItem>
                  </SelectContent></Select>
              </div>
            </div>

            <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 p-4 space-y-3" data-testid="apt-laundry-config">
              <div className="font-display font-bold text-indigo-900">Configurazione lavanderia (set per pulizia/check-in)</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.keys(LC_LABELS).map((k) => (
                  <div key={k}>
                    <Label className="text-xs">{LC_LABELS[k]}</Label>
                    <Input type="number" min="0" value={formA.laundry_config[k] ?? ""} onChange={(e) => updLC(k, e.target.value)} data-testid={`lc-${k}`} />
                  </div>
                ))}
              </div>
            </div>

            <div><Label>Note</Label><Textarea rows={2} value={formA.notes || ""} onChange={(e) => setFormA({ ...formA, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenA(false)}>Annulla</Button>
              <Button type="submit" className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]" data-testid="apt-submit">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={openP} onOpenChange={setOpenP}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editP ? "Modifica" : "Nuova"} struttura</DialogTitle></DialogHeader>
          <form onSubmit={saveP} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Label>Nome</Label><Input value={formP.name} onChange={(e) => setFormP({ ...formP, name: e.target.value })} required /></div>
            <div><Label>Città</Label><Input value={formP.city || ""} onChange={(e) => setFormP({ ...formP, city: e.target.value })} /></div>
            <div><Label>Indirizzo</Label><Input value={formP.address || ""} onChange={(e) => setFormP({ ...formP, address: e.target.value })} /></div>
            <div><Label>Proprietario</Label>
              <Select value={formP.owner_id || ""} onValueChange={(v) => setFormP({ ...formP, owner_id: v })}><SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><Label>Azienda gestore</Label><Input value={formP.company || ""} onChange={(e) => setFormP({ ...formP, company: e.target.value })} placeholder="PuraVida Home" /></div>
            <div className="md:col-span-2"><Label>Note</Label><Textarea rows={2} value={formP.notes || ""} onChange={(e) => setFormP({ ...formP, notes: e.target.value })} /></div>
            <DialogFooter className="md:col-span-2"><Button type="button" variant="outline" onClick={() => setOpenP(false)}>Annulla</Button><Button type="submit" className="bg-[var(--teal-deep)] hover:bg-[var(--teal-medium)]">Salva</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
